import FoodBankOutlinedIcon from '@mui/icons-material/FoodBankOutlined'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import Image from 'next/image'
import { useMemo } from 'react'
import AnimatedDiv from 'src/components/AnimatedDiv'
import { formatCurrency, formatNumber } from 'src/utils/functions'
import aggregatedValuesData from '../../../../public/fast_casual_index_json/aggregated_values_v2.json'
import AggregateDataListItem, {
  AggregateDataListItemProps
} from './AggregateDataListItem'

export default function AggregateDataListContainer() {
  const aggregatedData = useMemo(() => {
    const aggregateData = {
      auv: {
        data: {
          '<5': aggregatedValuesData['FC100 Brands with >=$1.5M AUV']['<5'],
          '5-25': aggregatedValuesData['FC100 Brands with >=$1.5M AUV']['5-25'],
          '25-100':
            aggregatedValuesData['FC100 Brands with >=$1.5M AUV']['25-100'],
          '100-500':
            aggregatedValuesData['FC100 Brands with >=$1.5M AUV']['100-500'],
          '>500': aggregatedValuesData['FC100 Brands with >=$1.5M AUV']['>500']
        }
      },
      rating_on_delivery: {
        data: {
          '<5': aggregatedValuesData['FC100 Brands Avg Rated >=4.5 on DD'][
            '<5'
          ],
          '5-25':
            aggregatedValuesData['FC100 Brands Avg Rated >=4.5 on DD']['5-25'],
          '25-100':
            aggregatedValuesData['FC100 Brands Avg Rated >=4.5 on DD'][
              '25-100'
            ],
          '100-500':
            aggregatedValuesData['FC100 Brands Avg Rated >=4.5 on DD'][
              '100-500'
            ],
          '>500':
            aggregatedValuesData['FC100 Brands Avg Rated >=4.5 on DD']['>500']
        }
      },
      reviews_on_delivery: {
        data: {
          '<5': aggregatedValuesData[
            'FC100 Brands with >=50 reviews/store on DD'
          ]['<5'],
          '5-25':
            aggregatedValuesData['FC100 Brands with >=50 reviews/store on DD'][
              '5-25'
            ],
          '25-100':
            aggregatedValuesData['FC100 Brands with >=50 reviews/store on DD'][
              '25-100'
            ],
          '100-500':
            aggregatedValuesData['FC100 Brands with >=50 reviews/store on DD'][
              '100-500'
            ],
          '>500':
            aggregatedValuesData['FC100 Brands with >=50 reviews/store on DD'][
              '>500'
            ]
        }
      },
      play_store: {
        data: {
          '<5': aggregatedValuesData[
            'FC100 Brands with >=4.5 Google Play Store Rating'
          ]['<5'],
          '5-25':
            aggregatedValuesData[
              'FC100 Brands with >=4.5 Google Play Store Rating'
            ]['5-25'],
          '25-100':
            aggregatedValuesData[
              'FC100 Brands with >=4.5 Google Play Store Rating'
            ]['25-100'],
          '100-500':
            aggregatedValuesData[
              'FC100 Brands with >=4.5 Google Play Store Rating'
            ]['100-500'],
          '>500':
            aggregatedValuesData[
              'FC100 Brands with >=4.5 Google Play Store Rating'
            ]['>500']
        }
      },
      apple_store: {
        data: {
          '<5': aggregatedValuesData[
            'FC100 Brands with >=4.5 Apple App Store Rating'
          ]['<5'],
          '5-25':
            aggregatedValuesData[
              'FC100 Brands with >=4.5 Apple App Store Rating'
            ]['5-25'],
          '25-100':
            aggregatedValuesData[
              'FC100 Brands with >=4.5 Apple App Store Rating'
            ]['25-100'],
          '100-500':
            aggregatedValuesData[
              'FC100 Brands with >=4.5 Apple App Store Rating'
            ]['100-500'],
          '>500':
            aggregatedValuesData[
              'FC100 Brands with >=4.5 Apple App Store Rating'
            ]['>500']
        }
      },
      instagram: {
        data: {
          '<5': aggregatedValuesData[
            'FC100 Brands with >=100K Instagram Followers'
          ]['<5'],
          '5-25':
            aggregatedValuesData[
              'FC100 Brands with >=100K Instagram Followers'
            ]['5-25'],
          '25-100':
            aggregatedValuesData[
              'FC100 Brands with >=100K Instagram Followers'
            ]['25-100'],
          '100-500':
            aggregatedValuesData[
              'FC100 Brands with >=100K Instagram Followers'
            ]['100-500'],
          '>500':
            aggregatedValuesData[
              'FC100 Brands with >=100K Instagram Followers'
            ]['>500']
        }
      },
      facebook: {
        data: {
          '<5': aggregatedValuesData['FC100 Brands with >=100K FB Followers'][
            '<5'
          ],
          '5-25':
            aggregatedValuesData['FC100 Brands with >=100K FB Followers'][
              '5-25'
            ],
          '25-100':
            aggregatedValuesData['FC100 Brands with >=100K FB Followers'][
              '25-100'
            ],
          '100-500':
            aggregatedValuesData['FC100 Brands with >=100K FB Followers'][
              '100-500'
            ],
          '>500':
            aggregatedValuesData['FC100 Brands with >=100K FB Followers'][
              '>500'
            ]
        }
      }
    }
    const getSum = (
      obj: (typeof aggregateData)[keyof typeof aggregateData]['data']
    ) => {
      return Object.values(obj).reduce((acc, val) => acc + val, 0)
    }

    // Filter out "<5": 0 from the aggregate values
    const filterZeroValues = (
      data: Record<string, number>
    ): Record<string, number> => {
      const filtered = { ...data }
      if (filtered['<5'] === 0) {
        delete filtered['<5']
      }
      return filtered
    }

    return [
      {
        logo: <StarRoundedIcon fontSize="inherit" sx={{ color: '#000000' }} />,
        title: 'AUV',
        label: formatNumber(getSum(aggregateData['auv'].data)),
        subLabel: 'brands in FC100 have',
        leftValue: formatCurrency(1500000, {
          maxFractionDigits: 1,
          notation: 'compact'
        }),
        valueText: 'AUV',
        rightValue: 'or more',
        helperLabel:
          'An AUV of $1.5M signals a powerful blend of operational efficiency, customer demand, and growth potential.',
        aggregateValues: filterZeroValues(aggregateData['auv'].data)
      },
      {
        logo: <FoodBankOutlinedIcon fontSize="inherit" />,
        title: 'Rating on 3PD',
        label: formatNumber(getSum(aggregateData['rating_on_delivery'].data)),
        subLabel: 'FC100 brands have avg rating',
        leftValue: formatNumber(4.5),
        valueLogo: (
          <StarRoundedIcon fontSize="inherit" sx={{ color: '#FAC666' }} />
        ),
        rightValue: 'or higher',
        helperLabel:
          'A 4.7+ 3PD rating reflects consistent quality and reliability in the most competitive delivery environment.',
        aggregateValues: filterZeroValues(
          aggregateData['rating_on_delivery'].data
        )
      },
      {
        logo: <FoodBankOutlinedIcon fontSize="inherit" />,
        title: 'Reviews on 3PD',
        label: formatNumber(getSum(aggregateData['reviews_on_delivery'].data)),
        subLabel: 'FC100 brands have',
        leftValue: formatNumber(50, { notation: 'compact' }),
        valueLogo: (
          <StarRoundedIcon fontSize="inherit" sx={{ color: '#FAC666' }} />
        ),
        valueText: 'reviews /store',
        rightValue: 'or higher',
        helperLabel:
          'With 50+ reviews per store, these brands demonstrate sustained engagement and a track record of customer satisfaction.',
        aggregateValues: filterZeroValues(
          aggregateData['reviews_on_delivery'].data
        )
      },
      {
        logo: (
          <Image
            src="/img/logos/short_play_store.svg"
            alt="Play Store"
            width={24}
            height={24}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ),
        title: 'Rating on Play Store',
        label: formatNumber(getSum(aggregateData['play_store'].data)),
        subLabel: 'FC100 brands have avg rating',
        leftValue: formatNumber(4.5),
        valueLogo: (
          <StarRoundedIcon fontSize="inherit" sx={{ color: '#FAC666' }} />
        ),
        rightValue: 'or higher',
        helperLabel:
          'A 4.7+ rating on Google Play indicates a seamless digital experience that users value and return for.',
        aggregateValues: filterZeroValues(aggregateData['play_store'].data)
      },
      {
        logo: (
          <Image
            src="/img/logos/short_apple.svg"
            alt="Apple Store"
            width={24}
            height={24}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ),
        title: 'Rating on Apple Store',
        label: formatNumber(getSum(aggregateData['apple_store'].data)),
        subLabel: 'FC100 brands have rating',
        leftValue: formatNumber(4.5),
        valueLogo: (
          <StarRoundedIcon fontSize="inherit" sx={{ color: '#FAC666' }} />
        ),
        rightValue: 'or higher',
        helperLabel:
          'Earning 4.8+ on the Apple App Store highlights a refined app experience that meets the expectations of discerning IOS users.',
        aggregateValues: filterZeroValues(aggregateData['apple_store'].data)
      },
      {
        logo: (
          <Image
            src="/img/logos/short_instagram.svg"
            alt="instagram"
            width={24}
            height={24}
            style={{ width: '100%', height: '100%' }}
          />
        ),
        title: 'Instagram Followers',
        label: formatNumber(getSum(aggregateData['instagram'].data)),
        subLabel: 'FC100 brands have',
        leftValue: formatNumber(100000, { notation: 'compact' }),
        valueLogo: (
          <GroupAddIcon fontSize="inherit" sx={{ color: '#F15412' }} />
        ),
        valueText: 'followers',
        rightValue: 'or more',
        helperLabel:
          'Crossing 100K Instagram followers signifies a strong brand narrative that connects deeply with audiences and drives loyalty.',
        aggregateValues: filterZeroValues(aggregateData['instagram'].data)
      },
      {
        logo: (
          <Image
            src="/img/logos/short_facebook.svg"
            alt="Facebook"
            width={24}
            height={24}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ),
        title: 'Facebook Followers',
        label: formatNumber(getSum(aggregateData['facebook'].data)),
        subLabel: 'brands in FC100 have',
        leftValue: formatNumber(100000, { notation: 'compact' }),
        valueLogo: (
          <GroupAddIcon fontSize="inherit" sx={{ color: '#F15412' }} />
        ),
        valueText: 'followers',
        rightValue: 'or more',
        helperLabel:
          'Reaching 100K followers on Facebook reflects an established community presence and ongoing brand resonance.',
        aggregateValues: filterZeroValues(aggregateData['facebook'].data)
      }
    ] as AggregateDataListItemProps[]
  }, [])

  return (
    <>
      <div
        // className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" // For 3 columns layout
        className="grid grid-cols-1 gap-4" // For 1 column layout
      >
        {aggregatedData.map((data, index) => (
          <AnimatedDiv
            key={index}
            className="row-span-1 flex items-center justify-center opacity-0"
            from={{ opacity: 0, translateY: 75 }}
            to={{ opacity: 1, translateY: 0, duration: 1, delay: 0 }}>
            <AggregateDataListItem {...data} />
          </AnimatedDiv>
        ))}
      </div>
    </>
  )
}
